import { doc, getDoc, updateDoc } from "firebase/firestore";
// import { db } from "./firebase.config"; // Adjust import path as needed
import toast from "react-hot-toast";
import { db } from "./firebase";

/**
 * Updates a payment service in the payments collection
 * @param {Object} updatedService - The complete service object with updates
 * @returns {Promise<boolean>} - Success status
 */
export const updatePaymentService = async (updatedService) => {
  try {
    const serviceDocRef = doc(db, "payments", updatedService.id);
    const docSnap = await getDoc(serviceDocRef);

    if (!docSnap.exists()) {
      toast.error("Servicio no encontrado");
      return false;
    }

    // Calculate if service is fully paid
    const totalPaid = (updatedService.payments || []).reduce(
      (sum, payment) => sum + payment.amount,
      0
    );

    updatedService.balance = updatedService.price - totalPaid;
    updatedService.hasBeenPaid = updatedService.balance === 0;

    // Update in Firestore
    await updateDoc(serviceDocRef, updatedService);

    toast.success("Servicio actualizado exitosamente");
    return true;
  } catch (error) {
    console.error("Error updating payment service:", error);
    toast.error("Error al actualizar el servicio");
    return false;
  }
};

/**
 * Adds a new payment to an existing service
 * @param {string} serviceId - The service ID
 * @param {Object} newPayment - The payment object to add
 * @returns {Promise<Object|null>} - Updated service or null if failed
 */
export const addPaymentToService = async (serviceId, newPayment) => {
  try {
    const serviceDocRef = doc(db, "payments", serviceId);
    const docSnap = await getDoc(serviceDocRef);

    if (!docSnap.exists()) {
      toast.error("Servicio no encontrado");
      return null;
    }

    const service = docSnap.data();

    // Add payment to the payments array
    const updatedPayments = [...(service.payments || []), newPayment];

    // Calculate new balance
    const totalPaid = updatedPayments.reduce(
      (sum, payment) => sum + payment.amount,
      0
    );
    const newBalance = service.price - totalPaid;

    // Update service
    const updatedService = {
      ...service,
      payments: updatedPayments,
      balance: newBalance,
      hasBeenPaid: newBalance === 0,
    };

    await updateDoc(serviceDocRef, updatedService);

    toast.success("Pago agregado exitosamente");
    return updatedService;
  } catch (error) {
    console.error("Error adding payment:", error);
    toast.error("Error al agregar el pago");
    return null;
  }
};

/**
 * Marks a service as fully paid by adding a payment for the remaining balance
 * @param {string} serviceId - The service ID
 * @param {Object} paymentDetails - Invoice, date, and description for the final payment
 * @returns {Promise<Object|null>} - Updated service or null if failed
 */
export const markServiceAsPaid = async (serviceId, paymentDetails) => {
  try {
    const serviceDocRef = doc(db, "payments", serviceId);
    const docSnap = await getDoc(serviceDocRef);

    if (!docSnap.exists()) {
      toast.error("Servicio no encontrado");
      return null;
    }

    const service = docSnap.data();

    if (service.hasBeenPaid) {
      toast.info("El servicio ya está marcado como pagado");
      return service;
    }

    // Create final payment for remaining balance
    const finalPayment = {
      id: `payment_${Date.now()}`,
      amount: service.balance,
      invoice: paymentDetails.invoice || "",
      date: paymentDetails.date || new Date().toISOString(),
      description: paymentDetails.description || "Pago completo",
    };

    const updatedPayments = [...(service.payments || []), finalPayment];

    const updatedService = {
      ...service,
      payments: updatedPayments,
      balance: 0,
      hasBeenPaid: true,
    };

    await updateDoc(serviceDocRef, updatedService);

    toast.success("Servicio marcado como pagado");
    return updatedService;
  } catch (error) {
    console.error("Error marking service as paid:", error);
    toast.error("Error al marcar el servicio como pagado");
    return null;
  }
};

/**
 * Updates service details (price, reason, description, date)
 * @param {string} serviceId - The service ID
 * @param {Object} updates - Object containing fields to update
 * @returns {Promise<Object|null>} - Updated service or null if failed
 */
export const updateServiceDetails = async (serviceId, updates) => {
  try {
    const serviceDocRef = doc(db, "payments", serviceId);
    const docSnap = await getDoc(serviceDocRef);

    if (!docSnap.exists()) {
      toast.error("Servicio no encontrado");
      return null;
    }

    const service = docSnap.data();

    // Recalculate balance if price changed
    let newBalance = service.balance;
    let hasBeenPaid = service.hasBeenPaid;

    if (updates.price !== undefined) {
      const totalPaid = (service.payments || []).reduce(
        (sum, payment) => sum + payment.amount,
        0
      );
      newBalance = updates.price - totalPaid;
      hasBeenPaid = newBalance === 0;
    }

    const updatedService = {
      ...service,
      ...updates,
      balance: newBalance,
      hasBeenPaid: hasBeenPaid,
    };

    await updateDoc(serviceDocRef, updatedService);

    toast.success("Servicio actualizado exitosamente");
    return updatedService;
  } catch (error) {
    console.error("Error updating service details:", error);
    toast.error("Error al actualizar el servicio");
    return null;
  }
};

/**
 * Deletes a specific payment from a service
 * @param {string} serviceId - The service ID
 * @param {string} paymentId - The payment ID to delete
 * @returns {Promise<Object|null>} - Updated service or null if failed
 */
export const deletePaymentFromService = async (serviceId, paymentId) => {
  try {
    const serviceDocRef = doc(db, "payments", serviceId);
    const docSnap = await getDoc(serviceDocRef);

    if (!docSnap.exists()) {
      toast.error("Servicio no encontrado");
      return null;
    }

    const service = docSnap.data();

    // Filter out the payment to delete
    const updatedPayments = (service.payments || []).filter(
      (payment) => payment.id !== paymentId
    );

    // Recalculate balance
    const totalPaid = updatedPayments.reduce(
      (sum, payment) => sum + payment.amount,
      0
    );
    const newBalance = service.price - totalPaid;

    const updatedService = {
      ...service,
      payments: updatedPayments,
      balance: newBalance,
      hasBeenPaid: newBalance === 0,
    };

    await updateDoc(serviceDocRef, updatedService);

    toast.success("Pago eliminado exitosamente");
    return updatedService;
  } catch (error) {
    console.error("Error deleting payment:", error);
    toast.error("Error al eliminar el pago");
    return null;
  }
};
